import { Suspense, lazy, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createBrowserRouter, RouterProvider, Outlet, useLocation, ScrollRestoration } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useThemeMode } from "./theme/theme-provider";
import "react-toastify/dist/ReactToastify.css";
import Help24x7 from "./components/Chatbox/Help24x7";
import AppInstallBanner from "./components/AppInstallBanner";
import ImpersonationBanner from "./components/impersonation-banner";

import Home from "./pages/Home/Home";
import { authActions, getUserInfoByToken } from "./store/authSlice";
import { onSessionExpired } from "./utils/session-expired-handler";
import checkLogined from "./utils/loader/checkLogined";
import changeBankInfoLoader from "./utils/loader/changeBankInfoLoader";
import withdrawLoader from "./utils/loader/withdrawLoader";
import withdrawHistoryLoader from "./utils/loader/withdrawHistoryLoader";
import protectUser from "./utils/loader/protectUser";
import FIndTikTokOrder from "./pages/Merchant/TikTok/FIndTikTokOrder";
import ErrorDefault from "./pages/Error/Error";
import Notification from "./pages/Notification";
import FindShopeeOrder from "./pages/Merchant/ShopeeLive/FindShopeeOrder";
import checkLiveUsers from "./utils/loader/checkLiveUsers";
import ShopeeLiveV2 from "./pages/Merchant/ShopeeLive/ShopeeLiveV2";
import Ref from "./pages/User/UserInfo/Ref";
import RejectOrderPost from "./pages/Merchant/Lazada/RejectOrderPost";
import LuckyWheel from "./pages/LuckyWheel/LuckyWheel";
import Trending from "./pages/Trending/Trending";
import SavedProducts from "./pages/SavedProducts/SavedProducts";
import PriceWatch from "./pages/PriceWatch/PriceWatch";
// import ShopeeVideoV2 from './pages/Merchant/ShopeeVideo/ShopeeVideoV2';
import ShopeeVideoMultiLink from "./pages/Merchant/ShopeeVideo/MultipleLink/MultipleLink";
import ShopeeLiveGuide from "./pages/Merchant/Shopee/ShopeeLiveGuide";
import ShopeeXu from "./pages/Merchant/ShopeeXu/ShopeeXu";
import Post from "./pages/Post/Post";
import PostMain from "./pages/Post/PostMain";
import PostPage from "./pages/Post/PostPage";
import PostEditor from "./pages/Post/PostEditor";
import VideoList from "./pages/Post/VideoList";
import FindShopeeVideo from "./pages/Merchant/Shopee/FindShopeeVideo";
import FindLazadaOrder from "./pages/Merchant/Lazada/FindLazadaOrder";
import ShopeeNew from "./pages/Merchant/Shopee/ShopeeNew";
import TikTokNew from "./pages/Merchant/TikTok/TikTokNew";
import LazadaNew from "./pages/Merchant/Lazada/LazadaNew";
import ShopeeFoodNew from "./pages/Merchant/ShopeeFood/ShopeeFoodNew";
import TopCashBackV2 from "./pages/Event/TopCashBackV2";
import TopOrdersV2 from "./pages/Event/TopOrdersV2";
import LinkConverter from "./pages/LinkConverter/LinkConverter";
import DownloadApp from "./pages/DownloadApp";
// Landing page HTML do admin tạo — render full-page trong iframe (không Header/Footer)
const LandingPage = lazy(() => import("./pages/LandingPage"));

const Category = lazy(() => import("./pages/Merchant/Shopee/Category"));
const Signup = lazy(() => import("./pages/Auth/Signup/Signup"));
const Login = lazy(() => import("./pages/Auth/Login/Login"));
const LoginAs = lazy(() => import("./pages/Auth/LoginAs/LoginAs"));
const ForgotPW = lazy(() => import("./pages/Auth/ForgotPW/ForgotPW"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Legal/Privacy"));
const Terms = lazy(() => import("./pages/Legal/Terms"));
const Support = lazy(() => import("./pages/Legal/Support"));

const User = lazy(() => import("./pages/User"));
const UserInfo = lazy(() => import("./pages/User/UserInfo/UserInfo"));
const ChangeEmail = lazy(() => import("./pages/User/UserInfo/ChangeEmail"));
const ChangePassword = lazy(() => import("./pages/User/UserInfo/ChangePassword"));
const ChangeUserName = lazy(() => import("./pages/User/UserInfo/ChangeName/ChangeUserName"));
const ChangeBankInfo = lazy(() => import("./pages/User/UserInfo/ChangeBankInfo"));
const ApplyReferral = lazy(() => import("./pages/User/UserInfo/ApplyRef/ApplyReferral"));
const VerifyEmail = lazy(() => import("./pages/User/UserInfo/VerifyEmail"));
const Order = lazy(() => import("./pages/User/Order"));
const Withdraw = lazy(() => import("./pages/User/Withdraw"));
const WithdrawHistory = lazy(() => import("./pages/User/WithdrawHistory"));
const CashHistory = lazy(() => import("./pages/User/CashHistory"));
const Redirect = lazy(() => import("./pages/Redirect/Redirect"));
// const Lazada = lazy(() => import('./pages/Merchant/Lazada/Lazada'));
// const TikTok = lazy(() => import('./pages/Merchant/TikTok'));
const Help = lazy(() => import("./pages/Help/index"));
const HelpCenter = lazy(() => import("./pages/Help/HelpCenter"));
const OrderHelperMain = lazy(() => import("./pages/Help/OrderHelper/OrderHelperMain"));
const OrderHelper = lazy(() => import("./pages/Help/OrderHelper/OrderHelper"));
const LostOrder = lazy(() => import("./pages/Help/OrderHelper/LostOrder"));
const CommissionIncorrect = lazy(() => import("./pages/Help/OrderHelper/CommissionIncorrect"));
const Guide = lazy(() => import("./pages/Help/Guide"));
const GuideMain = lazy(() => import("./pages/Help/Guide/GuideMain"));
const GetDisCount = lazy(() => import("./pages/Help/Guide/GetDiscount"));
const HowToChietKhauProWork = lazy(() => import("./pages/Help/Guide/HowToWork"));
const HowToWithdraw = lazy(() => import("./pages/Help/Guide/HowToWithDraw"));
const ExchangeVoucher = lazy(() => import("./pages/Voucher/ExchangeVoucher"));
const MyVouchers = lazy(() => import("./pages/Voucher/MyVouchers"));
const VoucherDetail = lazy(() => import("./pages/Voucher/VoucherDetail"));
const VoucherRegulations = lazy(() => import("./pages/Voucher/VoucherRegulations"));
const CommunityMain = lazy(() => import("./pages/Community/CommunityMain"));
// Chương trình thưởng (đợt X2, ngân sách...) — cùng API với tab Thưởng trên app
const RewardsMain = lazy(() => import("./pages/Rewards/RewardsMain"));
const RewardDetailPage = lazy(() => import("./pages/Rewards/RewardDetailPage"));
// /di-den?url=… — tạo link affiliate theo user rồi chuyển sang sàn (dùng cho link
// nằm trong nội dung tĩnh: điều khoản đợt thưởng, bài viết).
const GoToProductPage = lazy(() => import("./pages/Redirect/GoToProductPage"));
const CommunityPostPage = lazy(() => import("./pages/Community/CommunityPostPage"));
// Trang deal Flash Sale 1K — nặng (lưới ảnh + bộ lọc), tách bundle riêng
const FlashSale = lazy(() => import("./pages/FlashSale/FlashSale"));

// Root layout: renders all routes via Outlet + shows Help24x7 on non-home pages
function RootLayout() {
    const { pathname } = useLocation();
    return (
        <>
            <ScrollRestoration />
            <ImpersonationBanner />
            <Outlet />
            {pathname === "/" && <Help24x7 />}
            <AppInstallBanner />

        </>
    );
}

// Router defined outside App to ensure stable instance across re-renders
// (recreating the router resets ScrollRestoration state)
const router = createBrowserRouter([
        {
            element: <RootLayout />,
            children: [
        {
            path: "/",
            element: <Home />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "top_cash_back",
            element: <TopCashBackV2 />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "tai-app",
            element: <DownloadApp />,
            errorElement: <ErrorDefault />,
        },
        {
            // Trang tĩnh admin tự tạo ở Admin > Trang HTML (dùng cho utility block)
            path: "trang/:slug",
            element: (
                <Suspense>
                    <LandingPage />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "top_orders",
            element: <TopOrdersV2 />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "/error",
            element: <ErrorDefault />,
        },
        {
            path: "/signup",
            element: (
                <Suspense>
                    <Signup />
                </Suspense>
            ),
            loader: checkLogined,
            errorElement: <ErrorDefault />,
        },
        {
            path: "/login",
            element: (
                <Suspense>
                    <Login />
                </Suspense>
            ),
            loader: checkLogined,
            errorElement: <ErrorDefault />,
        },
        {
            path: "/login-as",
            element: (
                <Suspense>
                    <LoginAs />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "/forgot-password",
            element: (
                <Suspense>
                    <ForgotPW />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "/user-info",
            loader: protectUser,
            element: (
                <Suspense>
                    <User />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
            children: [
                {
                    index: true,
                    element: <UserInfo />,
                },
                {
                    path: "change-user-name",
                    element: <ChangeUserName />,
                },
                {
                    path: "change-email",
                    element: <ChangeEmail />,
                },
                {
                    path: "change-password",
                    element: <ChangePassword />,
                },
                {
                    path: "ref",
                    element: <Ref />,
                },
                {
                    path: "apply-ref",
                    element: <ApplyReferral />,
                },
                {
                    path: "verify-email",
                    element: <VerifyEmail />,
                },
                {
                    path: "change-bank-info",
                    element: <ChangeBankInfo />,
                    loader: changeBankInfoLoader,
                },
                {
                    path: "withdraw",
                    children: [
                        {
                            index: true,
                            element: (
                                <Suspense>
                                    <Withdraw />
                                </Suspense>
                            ),
                            loader: withdrawLoader,
                        },
                        {
                            path: "history",
                            element: (
                                <Suspense>
                                    <WithdrawHistory />
                                </Suspense>
                            ),
                            loader: withdrawHistoryLoader,
                        },
                    ],
                },
                {
                    path: "cash-history",
                    element: (
                        <Suspense>
                            <CashHistory />
                        </Suspense>
                    ),
                },
                {
                    path: "order",
                    element: <Order />,
                    errorElement: <ErrorDefault />,
                },
            ],
        },

        {
            path: "/shopee",
            children: [
                {
                    index: true,
                    element: <ShopeeNew />,
                    errorElement: <ErrorDefault />,
                },
                {
                    path: ":category",
                    element: (
                        <Suspense>
                            <Category />
                        </Suspense>
                    ),
                    errorElement: <ErrorDefault />,
                },
                {
                    path: "huongdanshopeelive",
                    element: <ShopeeLiveGuide />,
                },
                {
                    path: "find",
                    element: <FindShopeeVideo />,
                },
            ],
        },
        {
            path: "vongquayshopee",
            element: <ShopeeXu />,
        },
        {
            path: "/shopeefood",
            element: <ShopeeFoodNew />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "/lazada",
            children: [
                {
                    index: true,
                    element: <LazadaNew />,
                },
                {
                    path: "find",
                    element: <FindLazadaOrder />,
                },
                {
                    path: "reject-order",
                    element: <RejectOrderPost />,
                },
            ],
            errorElement: <ErrorDefault />,
        },
        {
            path: "/tiktok",

            children: [
                {
                    index: true,
                    element: <TikTokNew />,
                    errorElement: <ErrorDefault />,
                },
                {
                    path: "find",
                    element: <FIndTikTokOrder />,
                    errorElement: <ErrorDefault />,
                },
            ],
        },
        {
            path: "shopeelive",
            children: [
                {
                    index: true,
                    element: <ShopeeLiveV2 />,
                },
                {
                    path: "find",
                    element: <FindShopeeOrder />,
                    loader: checkLiveUsers,
                },
                {
                    path: "v2",
                    element: <ShopeeLiveV2 />,
                },
            ],
        },
        {
            path: "vid",
            element: <ShopeeVideoMultiLink />,
            errorElement: <ErrorDefault />,
            loader: protectUser,
        },
        {
            path: "shopeevideo",
            element: <ShopeeVideoMultiLink isFake={true} />,
            errorElement: <ErrorDefault />,
            loader: protectUser,
        },
        {
            path: "/redirect",
            element: (
                <Suspense>
                    <Redirect />
                </Suspense>
            ),
        },
        {
            path: "/help",
            element: (
                <Suspense>
                    <Help />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
            children: [
                {
                    index: true,
                    element: <HelpCenter />,
                },
                {
                    path: "order-helper",
                    element: (
                        <Suspense>
                            <OrderHelperMain />
                        </Suspense>
                    ),
                    children: [
                        {
                            element: <OrderHelper />,
                            index: true,
                        },
                        {
                            path: "lost",
                            element: <LostOrder />,
                        },
                        {
                            path: "incorrect-commission",
                            element: <CommissionIncorrect />,
                        },
                    ],
                },
                {
                    path: "guide",
                    element: (
                        <Suspense>
                            <GuideMain />
                        </Suspense>
                    ),
                    children: [
                        {
                            index: true,
                            element: <Guide />,
                        },
                        {
                            element: <GetDisCount />,
                            path: "how-to-get-discount",
                        },
                        {
                            element: <HowToChietKhauProWork />,
                            path: "how-to-work",
                        },
                        {
                            element: <HowToWithdraw />,
                            path: "how-to-withdraw",
                        },
                    ],
                },
            ],
        },
        {
            path: "/about",
            element: (
                <Suspense>
                    <About />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "/privacy",
            element: (
                <Suspense>
                    <Privacy />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "/terms",
            element: (
                <Suspense>
                    <Terms />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "/support",
            element: (
                <Suspense>
                    <Support />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "link-converter",
            element: <LinkConverter />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "notification",
            element: <Notification />,
            errorElement: <ErrorDefault />,
        },
        {
            path: "trending",
            element: <Trending />,
            errorElement: <ErrorDefault />,
        },
        {
            // Deal 1K/9K/29K — ô tiện ích "Flash Sale 1K" mở trang này (thay sansale.longhousee.net)
            path: "flash-sale",
            element: (
                <Suspense>
                    <FlashSale />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "di-den",
            element: (
                <Suspense>
                    <GoToProductPage />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "chuong-trinh-thuong",
            errorElement: <ErrorDefault />,
            children: [
                {
                    index: true,
                    element: (
                        <Suspense>
                            <RewardsMain />
                        </Suspense>
                    ),
                },
                {
                    path: ":slug",
                    element: (
                        <Suspense>
                            <RewardDetailPage />
                        </Suspense>
                    ),
                },
            ],
        },
        {
            path: "cong-dong",
            errorElement: <ErrorDefault />,
            children: [
                {
                    index: true,
                    element: (
                        <Suspense>
                            <CommunityMain />
                        </Suspense>
                    ),
                },
                {
                    path: ":postid",
                    element: (
                        <Suspense>
                            <CommunityPostPage />
                        </Suspense>
                    ),
                },
            ],
        },
        {
            path: "saved-products",
            element: <SavedProducts />,
            loader: protectUser,
            errorElement: <ErrorDefault />,
        },
        {
            path: "price-watch",
            element: <PriceWatch />,
            loader: protectUser,
            errorElement: <ErrorDefault />,
        },
        {
            path: "lucky-wheel",
            element: <LuckyWheel />,
            loader: protectUser,
        },
        {
            path: "voucher",
            element: (
                <Suspense>
                    <ExchangeVoucher />
                </Suspense>
            ),
            loader: protectUser,
            errorElement: <ErrorDefault />,
        },
        {
            path: "voucher/quy-dinh",
            element: (
                <Suspense>
                    <VoucherRegulations />
                </Suspense>
            ),
            errorElement: <ErrorDefault />,
        },
        {
            path: "voucher/my-vouchers",
            element: (
                <Suspense>
                    <MyVouchers />
                </Suspense>
            ),
            loader: protectUser,
            errorElement: <ErrorDefault />,
        },
        {
            path: "voucher/my-vouchers/:id",
            element: (
                <Suspense>
                    <VoucherDetail />
                </Suspense>
            ),
            loader: protectUser,
            errorElement: <ErrorDefault />,
        },
        {
            path: "post",
            element: <Post />,
            children: [
                {
                    path: ":postid",
                    element: <PostPage />,
                },
                {
                    index: true,
                    element: <PostMain />,
                },
                {
                    path: "post_editor",
                    element: <PostEditor />,
                },
                {
                    path: "post_editor/:postid",
                    element: <PostEditor />,
                },
                {
                    path: "videolist",
                    element: <VideoList />,
                },
            ],
        },
            ] // end RootLayout children
        },
]);

function App() {
    const dispatch = useDispatch();

    // Mất phiên đăng nhập (401 thật) → dọn state + báo rõ lý do + đưa về /login
    // bằng router.navigate (điều hướng mềm). Đăng ký 1 lần, router đã tạo ở trên.
    useEffect(() => {
        onSessionExpired(() => {
            dispatch(authActions.logout());
            toast.warn("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
            router.navigate("/login");
        });
    }, [dispatch]);

    useEffect(() => {
        if (localStorage.getItem("access_token")) {
            // Có token đã lưu → coi như ĐÃ đăng nhập ngay, không chờ getUserInfo.
            // getUserInfoByToken vẫn refresh profile; chỉ logout khi token sai (401).
            dispatch(authActions.login());
            dispatch(getUserInfoByToken());
        }
    }, [dispatch]);

    return (
        <>
            <RouterProvider router={router} />
            {/* Mount 1 lần để toast.* (react-toastify) hiển thị toàn app (trước đây thiếu → toast im lặng). */}
            <ThemedToastContainer />
        </>
    );
}


/**
 * react-toastify có prop `theme` sẵn — chỉ cần truyền đúng chế độ hiện hành.
 * Tách component con vì cần gọi hook.
 */
function ThemedToastContainer() {
    const { isDark } = useThemeMode();
    return (
        <ToastContainer
            position="top-center"
            autoClose={4000}
            newestOnTop
            theme={isDark ? "dark" : "light"}
        />
    );
}

export default App;
